/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import config from 'js/app/config';

import { Button, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import CourseraPlusLogoSvg from 'bundles/page/components/CourseraPlusLogoSvg';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/accomplishments';

const styles: Record<string, Interpolation<Theme>> = {
  courseraPlusAdContainer: () => css`
    .adContainer {
      position: relative;
      padding: 20px;
      background-color: var(--cds-color-blue-50);
      width: 100%;
      height: 606px;
      margin-top: 56px;
      margin-bottom: 56px;

      @media (min-width: 600px) {
        height: 299px;
        display: flex;
        padding: 26px;
      }

      .cPlus-image-wrapper-desktop {
        position: relative;
        padding-left: 25px;
        display: block;
        margin: auto;

        @media (max-width: 599px) {
          display: none;
          padding-left: 20px;
        }

        .coursera-plus-woman-desktop {
          position: relative;
          width: 63%;
        }

        .dots-desktop {
          position: absolute;
          width: 25%;
          bottom: 0%;
          right: 21%;
        }
      }

      .cPlus-text-wrapper {
        @media (min-width: 600px) {
          position: relative;
          max-width: 70%;
        }
      }

      .cPlus-image-wrapper {
        position: relative;
        padding-left: 40px;

        @media (min-width: 600px) {
          display: none;
        }

        .coursera-plus-woman {
          position: relative;
          width: 57%;
          margin: 0 auto 0 auto;
        }

        .dots {
          position: absolute;
          width: 19%;
          bottom: 5%;
          right: 30%;
        }
      }

      .title {
        position: relative;
        /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: bold;
        font-size: 24px;
        line-height: 30px;
        margin-bottom: 20px;

        @media (min-width: 600px) {
          font-size: 18px;
          margin-bottom: 10px;
        }

        @media (min-width: 768px) {
          margin-bottom: 20px;
        }
      }

      .caption {
        position: relative;
        /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: normal;
        font-size: 20px;
        line-height: 28px;
        margin-bottom: 20px;

        @media (min-width: 600px) {
          font-size: 16px;
        }
      }

      .cPlus-Logo {
        position: relative;
        max-width: 100%;
        height: auto;
        margin-bottom: 20px;

        @media (min-width: 600px) {
          max-width: 40%;
        }
      }

      .learn-button {
        position: absolute;
        width: calc(100% - 40px);
        max-width: none;
        bottom: 20px;

        @media (min-width: 600px) {
          width: 60%;
          bottom: 0%;
        }

        @media (min-width: 768px) {
          width: 44%;
        }
      }
    }
  `,
};

export const CourseraPlusAdContainer = () => {
  const courseraPlusWoman = `${config.url.resource_assets}coursera_plus/landing_page/header-image.png`;
  const dots = `${config.url.resource_assets}coursera_plus/landing_page/dots2.png`;
  const courseraPlusLink = `/courseraplus`;

  return (
    <section css={styles.courseraPlusAdContainer}>
      <TrackedDiv trackingName="cPlusAd" withVisibilityTracking>
        <div className="adContainer">
          <div className="cPlus-text-wrapper">
            <CourseraPlusLogoSvg className="cPlus-Logo" data-test="cPlus-Logo" />
            <Typography2 component="p" className="title">
              {_t('Try out different courses to see which one fits your needs')}
            </Typography2>
            <Typography2 component="p" className="caption">
              {_t(
                'Get a 7-day free trial that includes courses, Specializations, Projects, and Professional Certificates.'
              )}
            </Typography2>
            <div className="cPlus-image-wrapper">
              <img src={courseraPlusWoman} className="coursera-plus-woman" alt={_t('Woman looking at laptop')} />
              <img src={dots} className="dots" alt="" />
            </div>

            <Button
              className="learn-button"
              component={TrackedLink2}
              href={courseraPlusLink}
              role="link"
              target="_blank"
              trackingName="cPlus-AdButton"
              variant="primary"
              icon={<ArrowNextIcon className="svg-icon-accomplishments" />}
              aria-label={_t('Learn more about Coursera Plus')}
            >
              {_t('Learn More')}
            </Button>
          </div>

          <div className="cPlus-image-wrapper-desktop">
            <img src={courseraPlusWoman} className="coursera-plus-woman-desktop" alt={_t('Woman looking at laptop')} />
            <img src={dots} className="dots-desktop" alt="" />
          </div>
        </div>
      </TrackedDiv>
    </section>
  );
};
